import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      // Cancels out DockedContent's paddingBottom (MainDrawer.tsx) so this page
      // fills the full content area, matching pages that don't use HorizontalTabPage.
      // marginBottom: '-15px',
      backgroundColor: theme.palette.grey[100],
    },

    // Template header
    templateHeader: {
      backgroundColor: '#fff',
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.25, 2),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      flexShrink: 0,
    },
    backButton: {
      color: theme.palette.text.secondary,
      padding: 6,
      // Same small nudge-left on hover as the Enquiry form's header back link (headerBackLink in enquiryFormDrawerStyles.ts).
      '& .MuiSvgIcon-root': {
        transition: 'transform 0.15s ease',
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.primary,
        '& .MuiSvgIcon-root': {
          transform: 'translateX(-2px)',
        },
      },
      '@media (prefers-reduced-motion: reduce)': {
        '&:hover .MuiSvgIcon-root': {
          transform: 'none',
        },
      },
    },
    headerDivider: {
      width: 1,
      height: 28,
      backgroundColor: theme.palette.divider,
      marginRight: theme.spacing(0.25),
    },
    templateIcon: {
      width: 36,
      height: 36,
      borderRadius: 8,
      backgroundColor: '#e3f2fd',
      color: theme.palette.primary.main,
      flexShrink: 0,
    },
    headerText: {
      flex: 1,
      minWidth: 0,
    },
    templateTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    templateSubtitle: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      marginTop: 1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      flexShrink: 0,
    },
    headerActionButton: {
      textTransform: 'none',
      fontSize: 12,
      fontWeight: 500,
      borderRadius: 20,
      padding: '3px 12px',
      backgroundColor: 'rgba(21, 101, 192, 0.07)',
      color: theme.palette.primary.dark,
      '&:hover': {
        backgroundColor: 'rgba(21, 101, 192, 0.14)',
      },
    },
    headerActionIcon: {
      fontSize: 14,
    },

    // Tabs section
    tabsRoot: {
      backgroundColor: '#fff',
      borderBottom: `1px solid ${theme.palette.divider}`,
      flexShrink: 0,
    },
    tabs: {
      '& .MuiTab-root': {
        minWidth: 100,
        textTransform: 'none',
        fontSize: 13,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        letterSpacing: 0.2,
        padding: theme.spacing(1.5, 2.5),
        opacity: 1,
      },
      '& .Mui-selected': {
        color: theme.palette.primary.main,
      },
      '& .MuiTabs-indicator': {
        backgroundColor: theme.palette.primary.main,
        height: 2,
      },
    },
    tabIcon: {
      fontSize: 17,
      marginRight: theme.spacing(0.75),
      verticalAlign: 'middle',
    },

    // Content area
    contentArea: {
      flex: 1,
      minHeight: 0,
      overflow: 'auto',
      padding: 0,
    },

    // Placeholder
    placeholder: {
      border: `1.5px dashed ${theme.palette.grey[300]}`,
      borderRadius: 10,
      minHeight: 300,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1),
      backgroundColor: '#fff',
    },
    placeholderIcon: {
      fontSize: 40,
      color: theme.palette.grey[300],
    },
    placeholderText: {
      fontSize: 13,
      color: theme.palette.text.disabled,
    },
  })
);

// ─── TabPanel ────────────────────────────────────────────────────────────────

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

export const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    // display (not conditional rendering) toggles visibility so inactive tabs stay
    // mounted instead of unmounting — switching tabs no longer re-fetches data or
    // resets component state (search/filters/table state) in the hidden tab.
    style={{
        height: '100%',
        display: value === index ? 'block' : 'none',
    }}
  >
    <Box height="100%">{children}</Box>
  </div>
);

export function a11yProps(index: number) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

// ─── Tab definition ──────────────────────────────────────────────────────────

export interface TabDefinition {
  label: string;
  icon: React.ReactElement;
  content: React.ReactNode;
}

// ─── HorizontalTabPageProps ─────────────────────────────────────────────────────────

export interface HorizontalTabPageProps {
  /** Icon shown in the header avatar */
  icon: React.ReactElement;
  /** Main page title */
  title: string;
  /** Subtitle / description shown below the title */
  subtitle?: string;
  /** Tab definitions — label, icon, content */
  tabs: TabDefinition[];
  /** If provided, renders a back arrow that calls this handler */
  onBack?: () => void;
  /** Tooltip text for the back button (default: "Back") */
  backTooltip?: string;
  /** If provided, renders a "Learn More" button on the right of the header */
  onLearnMore?: () => void;
  /** If provided, renders a "Resource Centre" button on the right of the header */
  onResourceCenter?: () => void;
  /** Controlled active tab index */
  activeTab?: number;
  /** Called when selected tab changes */
  onTabChange?: (newValue: number) => void;
}

// ─── HorizontalTabPage ──────────────────────────────────────────────────────────────

export const HorizontalTabPage: React.FC<HorizontalTabPageProps> = ({
  icon,
  title,
  subtitle,
  tabs,
  onBack,
  backTooltip = 'Back',
  onLearnMore,
  onResourceCenter,
  activeTab,
  onTabChange,
}) => {
  const classes = useStyles();
  const [uncontrolledActiveTab, setUncontrolledActiveTab] = useState(activeTab ?? 0);
  const currentActiveTab = activeTab ?? uncontrolledActiveTab;
  // Tabs mount lazily on first visit, then stay mounted (hidden via CSS) so
  // switching back to a previously-visited tab doesn't remount/re-fetch it.
  const [visitedTabs, setVisitedTabs] = useState<number[]>([currentActiveTab]);

  useEffect(() => {
    setVisitedTabs(prev => prev.includes(currentActiveTab) ? prev : [...prev, currentActiveTab]);
  }, [currentActiveTab]);

  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    if (activeTab === undefined) {
      setUncontrolledActiveTab(newValue);
    }
    onTabChange?.(newValue);
    setVisitedTabs(prev => prev.includes(newValue) ? prev : [...prev, newValue]);
  };

  return (
    <div className={classes.root}>

      {/* ── Header ── */}
      <div className={classes.templateHeader}>

        {/* Optional back button */}
        {onBack && (
          <>
            <Tooltip title={backTooltip} placement="bottom">
              <IconButton
                className={classes.backButton}
                size="small"
                onClick={onBack}
                aria-label={backTooltip}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <div className={classes.headerDivider} />
          </>
        )}

        {/* Icon + text */}
        <Avatar className={classes.templateIcon} variant="rounded">
          {icon}
        </Avatar>

        <div className={classes.headerText}>
          <Typography className={classes.templateTitle}>{title}</Typography>
          {subtitle && (
            <Typography className={classes.templateSubtitle}>{subtitle}</Typography>
          )}
        </div>

        {(onLearnMore || onResourceCenter) && (
          <div className={classes.headerActions}>
            {onLearnMore && (
              <Button
                variant="text"
                size="small"
                onClick={onLearnMore}
                startIcon={<VideoLibraryOutlinedIcon className={classes.headerActionIcon} />}
                className={classes.headerActionButton}
              >
                Learn More
              </Button>
            )}
            {onResourceCenter && (
              <Button
                variant="text"
                size="small"
                onClick={onResourceCenter}
                startIcon={<HelpOutlineIcon className={classes.headerActionIcon} />}
                className={classes.headerActionButton}
              >
                Resource Centre
              </Button>
            )}
          </div>
        )}

      </div>

      {/* ── Tabs ── */}
      <Paper className={classes.tabsRoot} square elevation={0}>
        <Tabs
          value={currentActiveTab}
          onChange={handleTabChange}
          className={classes.tabs}
          aria-label={`${title} tabs`}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, i) => (
            <Tab
              key={i}
              label={
                <span>
                  {React.cloneElement(tab.icon, { className: classes.tabIcon })}
                  {tab.label}
                </span>
              }
              {...a11yProps(i)}
            />
          ))}
        </Tabs>
      </Paper>

      {/* ── Content ── */}
      <div className={classes.contentArea}>
        {tabs.map((tab, i) => (
          visitedTabs.includes(i) && (
            <TabPanel key={i} value={currentActiveTab} index={i}>
              {tab.content}
            </TabPanel>
          )
        ))}
      </div>

    </div>
  );
};

// example how to use it
// import React from 'react';

// // ─── Example usage ───────────────────────────────────────────────────────────
// // Below shows how to consume HorizontalTabPage for two different pages.
// // Remove this section once you integrate into your own routing setup.

// import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
// import AutorenewOutlinedIcon      from '@material-ui/icons/AutorenewOutlined';
// import { HorizontalTabPage, TabDefinition } from '../v2/layouts/HorizontalTabPage';
// import { MarketingRuleSetupPage } from './ruleSetup/MarketingRuleSetupPage';
// import { MarketingAutomationQueuePage } from './automationQueue/MarketingAutomationQueuePage';
// import { UserFeature, UserFeatureEnum } from '../types/UserFeature';
// import SyncOutlinedIcon from '@material-ui/icons/SyncOutlined';
// import MailOutlineOutlinedIcon from '@material-ui/icons/MailOutlineOutlined';

// // --- Page: Marketing Automation (no back button example) ---

// const marketingTabs: TabDefinition[] = [
//     ...(UserFeature.HasAccess(UserFeatureEnum.hasMarketingAutomationQueueSummary) ? [
//         {
//             label: 'Process Queue',
//             icon: <SyncOutlinedIcon />,
//             content: (
//             <div 
//                 style={{ 
//                     border: '1.5px dashed #d1d5db', 
//                     borderRadius: 10, 
//                     display: 'flex', 
//                     flexDirection: 'column', 
//                     background: '#fff', 
//                     height: '100%' 
//                 }}>
//                 <MarketingAutomationQueuePage />
//             </div>
//             ),
//         },
//     ] : []),
//      ...(UserFeature.HasAccess(UserFeatureEnum.hasMarketingAutomationRuleSummary) ? [
//         {
//             label: 'Rule Configuration',
//             icon: <SettingsOutlinedIcon />,
//             content: (
//             <div 
//                 style={{ 
//                     border: '1.5px dashed #d1d5db', 
//                     borderRadius: 10, 
//                     display: 'flex', 
//                     flexDirection: 'column', 
//                     background: '#fff', 
//                     height: '100%' 
//                 }}>
//                 <MarketingRuleSetupPage />
//             </div>
//             ),
//         },
//     ] : []),
//     {
//         label: 'Matter Template Setup',
//         icon: <MailOutlineOutlinedIcon />,
//         content: (
//         <div style={{ border: '1.5px dashed #d1d5db', borderRadius: 10, minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', color: '#9ca3af', fontSize: 13 }}>
//             Matter Template Setup content goes here
//         </div>
//         ),
//     },
// ];

// export const MarketingAutomationPage: React.FC = () => (
//   <HorizontalTabPage
//     icon={<AutorenewOutlinedIcon fontSize="small" />}
//     title="Marketing Automation"
//     subtitle="Manage your marketing automation rules and process queue"
//     tabs={marketingTabs}
//     onBack={() => console.log('Back clicked')} // Example back button handler
//     // no onBack prop → back button is hidden
//   />
// );